import { useEffect, useMemo, useRef, useState } from "react"
import { Grid } from "@mui/material"
import { DateTime } from "luxon"
import { MRT_ColumnDef, MRT_RowSelectionState, MRT_TableInstance } from "material-react-table"
import { faFileArrowDown } from "@fortawesome/pro-light-svg-icons"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { ApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { ActionBarItem, FloatingActionBar } from "../../../../../../shared/components/table/FloatingActionBar"
import { useSupplier } from "../../../../supplierHooks"
import { SUPPLIER_REPORTS_ROUTE } from "../../../../../../constants/routes"
import {
    TableNumberOfItemsBox,
    TableSummaryWrap,
    TableTotalAmountFromAPIBox,
} from "../../../../../../shared/components/table/TableSummaryBox"
import { fetchCustomerSales, SupplierCustomerSalesQuery } from "../customerSalesApi"
import { CurrencyCode, MerchantSalesMerchants } from "../../merchantSales/merchantSalesTypes"
import { SelectShopType } from "../../merchantSales/components/SelectShopType"
import { useExportCustomerSales } from "../hooks/useExportCustomerSales"
import { DetailedPanel } from "../components/DetailedPanel"
import { StandardExportButton } from "../../../../../../shared/components/table/StandardExportButton"
import { StandardTwoLinesCell } from "../../../../../../shared/components/table/StandardTwoLinesCell"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { useColumnFactory } from "../../../../../../core/api/useTableColumns"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { StandardPaymentMethodsFilter } from "../../../../../../shared/components/filters/components/StandardPaymentMethodsFilter"
import { useOrderTypeFilter } from "../../../../../../shared/components/filters/orderTypeFilter/useOrderTypeState"
import { StandardOrderTypeFilter } from "../../../../../../shared/components/filters/orderTypeFilter/StandardOrderTypeFilter"
import {
    DateType,
    SelectDateTypeFilter,
} from "../../../../../../shared/components/filters/selectDateTypeFilter/SelectDateTypeFilter"
import { orderFilterButtonConfig } from "../../../../../../shared/components/filters/orderTypeFilter/helpers"
import { type DtDateRange, StandardDateRangeFilter } from "../../../../../../shared/components/table/StandardDateRangeFilter"

export function SupplierCustomerSales() {
    const { supplierId } = useSupplier()

    const { l10n } = useAppLocalization()

    const [dateType, setDateType] = useState<DateType>(DateType.ORDER_DATE)
    const [paymentMethods, setPaymentMethods] = useState<Array<string>>([])
    const [shopTypes, setShopTypes] = useState<Array<string>>([])
    const { orderType, setOrderType } = useOrderTypeFilter()
    const { startDate, endDate } = useInitialDateFilters()
    const [dateState, setDateState] = useState<DtDateRange>({
        startDate: startDate,
        endDate: endDate,
    })

    const [selectedRows, setSelectedRows] = useState<MerchantSalesMerchants[]>()
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})

    const tableInstanceRef = useRef<MRT_TableInstance<MerchantSalesMerchants>>(null)

    useEffect(() => {
        if (Object.keys(rowSelection).length > 0) {
            setSelectedRows(tableInstanceRef.current?.getSelectedRowModel().rows.map((row) => row.original))
        } else {
            setSelectedRows(undefined)
        }
    }, [rowSelection])

    const customerSalesResult = useApiQuery(fetchCustomerSales, {
        queryName: SupplierCustomerSalesQuery.CUSTOMER_SALES_FOR_SUPPLIER,
        dependencies: {
            supplierId,
            shopTypes: shopTypes.join(","),
            paymentMethods: paymentMethods.join(","),
            startDate: dateState.startDate.toISODate(),
            endDate: dateState.endDate.toISODate(),
            orderType,
            dateType,
        },
    })

    const rowData = useMemo(() => customerSalesResult.data?.merchants ?? [], [customerSalesResult?.data?.merchants])

    const handleClearSelected = () => {
        setSelectedRows(undefined)
        setRowSelection({})
    }

    const { handleExportRows } = useExportCustomerSales(dateState.startDate.toISODate(), dateState.endDate.toISODate())

    const columnFactory = useColumnFactory<MerchantSalesMerchants>()

    const columns = useMemo<MRT_ColumnDef<MerchantSalesMerchants>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("supplier-customer-sales-table-name-column"),
                accessorKey: "name",
                Cell: ({ row }) => (
                    <StandardTwoLinesCell title={row.original.name} subtitle1={row.original.address.displayName} />
                ),
            }),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("supplier-customer-sales-table-upcoming-sales-column", {
                        currencyCode: CurrencyCode.DKK,
                    }),
                },
                (row) => row.upcomingSales,
            ),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("supplier-customer-sales-table-booked-sales-column", {
                        currencyCode: CurrencyCode.DKK,
                    }),
                },
                (row) => row.bookedSales,
            ),
        ],
        [l10n, columnFactory],
    )

    return (
        <>
            <LocalizedStrict id="supplier-customer-sales-header" attrs={{ title: true }}>
                <PageHeader page={Page.SUPPLIER_CUSTOMER_SALES} title="Customer Sales">
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="supplier-nav-item-home">
                            <BreadcrumbLink to={SUPPLIER_REPORTS_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-nav-item-accounting">
                            <Breadcrumb>Accounting</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-nav-item-customer-sales">
                            <Breadcrumb>Customer sales</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <StandardOrderTypeFilter
                orderType={orderType}
                setOrderType={setOrderType}
                config={orderFilterButtonConfig}
            />

            <Grid container direction="column">
                <ApiQueryTable
                    isLoading={customerSalesResult.isInitialLoading}
                    isFetching={customerSalesResult.isFetching}
                    isError={customerSalesResult.isError}
                    data={rowData}
                    tableInstanceRef={tableInstanceRef}
                    enableExpandAll={false}
                    enableExpanding
                    columns={columns}
                    getRowId={(originalRow, index) => {
                        if (!originalRow.id) return index.toString()

                        return originalRow.id.toString()
                    }}
                    state={{
                        rowSelection,
                    }}
                    renderDetailPanel={({ row }) => (
                        <DetailedPanel customerId={row.original.id} shopTypes={shopTypes} />
                    )}
                    onRowSelectionChange={setRowSelection}
                    enableRowSelection
                    initialState={{ density: "spacious" }}
                    renderToolbarInternalActions={({ table }) => (
                        <StandardTableFilter variant="right" table={table}>
                            <StandardExportButton
                                onClick={() => handleExportRows(tableInstanceRef.current?.getRowModel().rows)}
                            />
                        </StandardTableFilter>
                    )}
                    renderTopToolbarCustomActions={() => (
                        <StandardTableFilter variant="left">
                            <SelectDateTypeFilter dateType={dateType} setDateType={setDateType} />
                            <StandardDateRangeFilter value={dateState} onChange={setDateState} />
                            <SelectShopType shopTypes={shopTypes} setShopTypes={setShopTypes} />
                            <StandardPaymentMethodsFilter
                                value={paymentMethods}
                                setValue={setPaymentMethods}
                                size="small"
                            />
                        </StandardTableFilter>
                    )}
                    renderBottomToolbarCustomActions={() => (
                        <TableSummaryWrap>
                            <LocalizedStrict id="table-summary-box-number-of-orders" attrs={{ title: true }}>
                                <TableNumberOfItemsBox
                                    data={rowData}
                                    isLoading={customerSalesResult.isLoading}
                                    title="Number of orders"
                                />
                            </LocalizedStrict>

                            <TableTotalAmountFromAPIBox totalAmount={customerSalesResult.data?.totalBookedSales} />
                        </TableSummaryWrap>
                    )}
                />
            </Grid>

            {selectedRows && (
                <FloatingActionBar selectedItemsLength={selectedRows.length} handleCloseActionBar={handleClearSelected}>
                    <LocalizedStrict
                        id="supplier-customer-sales-table-floating-action-bar-export"
                        attrs={{ title: true }}
                    >
                        <ActionBarItem
                            icon={faFileArrowDown}
                            title="Export"
                            onClick={() => handleExportRows(tableInstanceRef.current?.getSelectedRowModel().rows)}
                        />
                    </LocalizedStrict>
                </FloatingActionBar>
            )}
        </>
    )
}
